/**
 * Kindtrim storefront behaviour: before/after compare sliders and card sliders.
 * Progressive enhancement only; every section renders and scrolls without this file.
 */

/**
 * Drag-to-compare before/after images.
 * Markup: <kt-before-after> containing an <input type="range" class="kt-ba__range">.
 */
class KtBeforeAfter extends HTMLElement {
  connectedCallback() {
    this.range = this.querySelector('.kt-ba__range');
    if (!this.range) return;

    this.update = () => this.style.setProperty('--kt-pos', `${this.range.value}%`);
    this.range.addEventListener('input', this.update);
    this.update();
  }

  disconnectedCallback() {
    this.range?.removeEventListener('input', this.update);
  }
}

/**
 * Horizontal scroll-snap slider with previous/next buttons and a "1 / N" counter.
 * Markup: <kt-slider> containing [data-kt-slider-track] with [data-kt-slide] children,
 * plus optional [data-kt-slider-prev], [data-kt-slider-next] and [data-kt-slider-current].
 */
class KtSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-kt-slider-track]');
    if (!this.track) return;

    this.prevButton = this.querySelector('[data-kt-slider-prev]');
    this.nextButton = this.querySelector('[data-kt-slider-next]');
    this.current = this.querySelector('[data-kt-slider-current]');

    this.onPrev = () => this.go(-1);
    this.onNext = () => this.go(1);
    this.onScroll = () => {
      window.cancelAnimationFrame(this.frame);
      this.frame = window.requestAnimationFrame(() => this.sync());
    };

    this.prevButton?.addEventListener('click', this.onPrev);
    this.nextButton?.addEventListener('click', this.onNext);
    this.track.addEventListener('scroll', this.onScroll, { passive: true });

    // Reading scroll positions forces a layout, so do it when the slider comes near the screen rather than while
    // the page is loading. The markup already starts in the synced state (first slide, Previous disabled).
    this.observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.observer?.disconnect();
        this.sync();
      },
      { rootMargin: '400px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.prevButton?.removeEventListener('click', this.onPrev);
    this.nextButton?.removeEventListener('click', this.onNext);
    this.track?.removeEventListener('scroll', this.onScroll);
  }

  get slides() {
    return Array.from(this.track.querySelectorAll('[data-kt-slide]'));
  }

  /** Index of the first slide whose start edge is at or past the scroll position. */
  get index() {
    const { slides, track } = this;
    const start = track.getBoundingClientRect().left;
    const position = slides.findIndex((slide) => slide.getBoundingClientRect().left - start >= -8);
    return position === -1 ? slides.length - 1 : position;
  }

  /** @param {number} direction */
  go(direction) {
    const { slides, track } = this;
    const target = slides[Math.min(Math.max(this.index + direction, 0), slides.length - 1)];
    if (!target) return;

    const offset = target.getBoundingClientRect().left - track.getBoundingClientRect().left;
    track.scrollBy({ left: offset, behavior: 'smooth' });
  }

  sync() {
    const { track } = this;
    const atStart = track.scrollLeft <= 4;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;

    if (this.prevButton) this.prevButton.disabled = atStart;
    if (this.nextButton) this.nextButton.disabled = atEnd;
    if (this.current) this.current.textContent = String(atEnd ? this.slides.length : this.index + 1);
  }
}

if (!customElements.get('kt-before-after')) customElements.define('kt-before-after', KtBeforeAfter);
if (!customElements.get('kt-slider')) customElements.define('kt-slider', KtSlider);

/**
 * Quick-answer chips in the buy box: open the matching FAQ answer and bring it into view.
 * Without JavaScript the chips are plain anchor links to the FAQ.
 */
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('[data-kt-faq-link]') : null;
  if (!link) return;

  const id = decodeURIComponent((link.getAttribute('href') || '').replace(/^#/, ''));
  const item = id ? document.getElementById(id) : null;
  if (!(item instanceof HTMLDetailsElement)) return;

  event.preventDefault();
  item.open = true;
  item.classList.remove('kt-faq__item--flash');
  void item.offsetWidth;
  item.classList.add('kt-faq__item--flash');

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  item.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
  item.querySelector('summary')?.focus({ preventScroll: true });
});

const landingImages = new IntersectionObserver(entries => {
  for (const entry of entries) {
    if (!entry.isIntersecting) continue;
    const image = entry.target;
    image.fetchPriority = 'low';
    image.loading = 'eager';
    if (image.dataset.ktSrcset) image.srcset = image.dataset.ktSrcset;
    image.src = image.dataset.ktSrc;
    image.removeAttribute('data-kt-src');
    image.removeAttribute('data-kt-srcset');
    image.removeAttribute('data-kt-lazy');
    landingImages.unobserve(image);
  }
}, { rootMargin: '1200px 0px' });

const observeLandingImages = root => {
  if (!(root instanceof Element || root instanceof Document)) return;
  if (root instanceof Element && root.matches('img[data-kt-lazy]')) landingImages.observe(root);
  for (const image of root.querySelectorAll('img[data-kt-lazy]')) landingImages.observe(image);
};
observeLandingImages(document);
document.addEventListener('shopify:section:load', event => observeLandingImages(event.target));


/**
 * Scrolling strip: pause the loop while it is off screen. The strip sits far down the page, and a
 * running loop makes the browser redo frame work on every frame, which slows the page on phones.
 * It restarts a screen before it comes into view and carries on from where it paused.
 */
const marquees = new IntersectionObserver(entries => {
  for (const entry of entries) {
    const track = entry.target.querySelector('.kt-marquee__track');
    if (track instanceof HTMLElement) track.style.animationPlayState = entry.isIntersecting ? '' : 'paused';
  }
}, { rootMargin: '100% 0px' });

const observeMarquees = root => {
  if (!(root instanceof Element || root instanceof Document)) return;
  if (root instanceof Element && root.matches('.kt-marquee__viewport')) marquees.observe(root);
  for (const viewport of root.querySelectorAll('.kt-marquee__viewport')) marquees.observe(viewport);
};
observeMarquees(document);
document.addEventListener('shopify:section:load', event => observeMarquees(event.target));
